import React from 'react'
import { makeStyles, Grid, Hidden, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import type { ColorTileProps } from '../ColorTile'
import ColorTile from '../ColorTile'
import MobileColorTile from '../ColorTile/MobileColorTile'

const CUSTOM_FONT_SW = 'CustomFont-SW'
const FLEX_START = 'flex-start'

const useStyles = makeStyles<Theme, ContentFeatureProps>(
  (theme) => ({
    root: {
      padding: '40px',
      [theme.breakpoints.down('sm')]: {
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column-reverse',
        padding: '20px'
      }
    },
    content: (props) => ({
      justifyContent: 'center',
      alignItems: FLEX_START,
      [theme.breakpoints.down('sm')]: {
        alignItems: 'center'
      },
      [theme.breakpoints.up('xl')]: {
        marginBottom: '100px'
      },
      // At iPad widths the text sits flush-left inside its column. When the
      // layout is reversed (text on the right of the row) that puts text right
      // up against the color tiles — give it some breathing room on that side.
      ...(props.reverseOrder && {
        [theme.breakpoints.between('sm', 'md')]: {
          paddingLeft: '96px'
        }
      })
    }),
    cta: {
      marginTop: '10px',
      width: '75%',
      [theme.breakpoints.down('lg')]: {
        justifyContent: 'center'
      },
      [theme.breakpoints.down('md')]: {
        width: '100%'
      },
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        maxWidth: '400px'
      }
    },
    colorTiles: (props) => ({
      flexDirection: 'column',
      alignItems: FLEX_START,
      [theme.breakpoints.down('lg')]: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        // first child has justify-self: flex-end, second child justify-self: flex-start
        '& > *:first-child': {
          justifySelf: 'flex-end',
          paddingRight: '8px'
        },
        '& > *:last-child': {
          justifySelf: FLEX_START
        }
      },
      [theme.breakpoints.down(372)]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        alignSelf: 'center',
        width: 'fit-content',
        gap: '8px',
        '& > *:last-child': {
          marginLeft: props.colorTileB.marginLeft || '0'
        }
      }
    }),
    title: {
      fontFamily: CUSTOM_FONT_SW,
      fontSize: '44px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '48px',
      letterSpacing: '-1.5px',
      textAlign: 'left',
      [theme.breakpoints.down('sm')]: {
        textAlign: 'center',
        maxWidth: '500px'
      }
    },
    description: {
      fontFamily: CUSTOM_FONT_SW,
      fontSize: '20px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '24px',
      letterSpacing: '-0.5px',
      textAlign: 'left',
      [theme.breakpoints.down('sm')]: {
        textAlign: 'center',
        maxWidth: '500px'
      }
    },
    image: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      [theme.breakpoints.down('md')]: {
        width: '315px',
        height: '350px',
        maxWidth: '100%',
        objectFit: 'cover',
        display: 'block',
        margin: '0 auto'
      }
    }
  }),
  { name: 'ContentFeature' }
)

export interface ContentFeatureProps {
  title: React.ReactNode
  description: React.ReactNode
  cta: React.ReactNode
  colorTileA: ColorTileProps
  colorTileB: ColorTileProps
  image: string
  imageAlt: string
  reverseOrder?: boolean
}

function ContentFeature(props: ContentFeatureProps) {
  const classes = useStyles(props)

  const { title, description, cta, colorTileA, colorTileB, image, imageAlt, reverseOrder } = props
  return (
    <Grid
      container
      className={classes.root}
      spacing={2}
      direction={reverseOrder ? 'row-reverse' : 'row'}
      justifyContent="space-between"
    >
      <Grid item container md={5} className={classes.content} direction="column">
        <Grid item>
          <Typography variant="h2" className={classes.title}>
            {title}
          </Typography>
        </Grid>
        <Grid item>
          <Typography variant="body1" className={classes.description}>
            {description}
          </Typography>
        </Grid>
        <Grid item container className={classes.cta} spacing={1}>
          {cta}
        </Grid>
      </Grid>
      <Grid item container md={2} className={classes.colorTiles} spacing={1}>
        <Hidden smDown>
          <Grid item>
            <ColorTile
              color={colorTileA.color}
              colorCode={colorTileA.colorCode}
              colorName={colorTileA.colorName}
              darkText={colorTileA.darkText}
            />
          </Grid>
          <Grid item>
            <ColorTile
              color={colorTileB.color}
              colorCode={colorTileB.colorCode}
              colorName={colorTileB.colorName}
              darkText={colorTileB.darkText}
            />
          </Grid>
        </Hidden>
        <Hidden mdUp>
          <MobileColorTile
            color={colorTileA.color}
            colorCode={colorTileA.colorCode}
            colorName={colorTileA.colorName}
            darkText={colorTileA.darkText}
          />
          <MobileColorTile
            color={colorTileB.color}
            colorCode={colorTileB.colorCode}
            colorName={colorTileB.colorName}
            darkText={colorTileB.darkText}
          />
        </Hidden>
      </Grid>
      <Grid item md={5}>
        <img src={image} alt={imageAlt} className={classes.image} />
      </Grid>
    </Grid>
  )
}

export default ContentFeature
